/**
 * useTableSession — public-facing table session state for pay-at-end mode.
 * Manages: session init, start, refresh, bill request.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import i18n from "@/i18n";
import { mergeTableSessionWithDeviceIdentity } from "@/utils/tableSessionIdentity";

export function useTableSession({ tablePayAtEnd, tableId, restaurant, API, tableCurrentSessionId }) {
  const [tableSession, setTableSession] = useState(null);
  const [showTableSessionModal, setShowTableSessionModal] = useState(false);
  const [showTableOrdersModal, setShowTableOrdersModal] = useState(false);
  const [tableSessionLoading, setTableSessionLoading] = useState(false);
  const [billRequested, setBillRequested] = useState(false);
  const [requestingBill, setRequestingBill] = useState(false);

  // Only the table's shared-account setting starts a customer session.
  // NFC payment-at-table is an attendant-only flow and is unrelated here.
  const isPayAtEnd = tablePayAtEnd;
  useEffect(() => {
    // tableId is set from the menu response — truthy means menu has loaded.
    // tableCurrentSessionId values:
    //   undefined = old backend (no feature) or menu still loading
    //   null      = backend confirmed no active session on this table
    //   string    = backend's current active session ID for this table
    if (!isPayAtEnd || !tableId) return;

    // If user previously chose view-only for this table in this browser session, skip modal
    const viewOnly = sessionStorage.getItem(`kaiso_table_view_only_${tableId}`);
    if (viewOnly) return;

    const saved = localStorage.getItem(`kaiso_table_session_${tableId}`);
    if (!saved) {
      setShowTableSessionModal(true);
      return;
    }

    try {
      const sessionData = JSON.parse(saved);

      // Identity caches written before the per-device isolation fix may
      // contain the first diner's PII copied from the shared session. Force a
      // one-time re-identification instead of trusting contaminated storage.
      if (sessionData.identity_scope !== "device") {
        localStorage.removeItem(`kaiso_table_session_${tableId}`);
        setShowTableSessionModal(true);
        return;
      }

      // Auto-expire sessions older than 12 hours (eliminates stuck test/abandoned sessions)
      const SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;
      if (sessionData.created_at && Date.now() - new Date(sessionData.created_at).getTime() > SESSION_MAX_AGE_MS) {
        localStorage.removeItem(`kaiso_table_session_${tableId}`);
        setShowTableSessionModal(true);
        return;
      }

      // Fast-invalidation (new backend only): if backend says this table's current
      // session differs from the cached one, the cached session is stale.
      if (tableCurrentSessionId !== undefined && sessionData.session_id !== tableCurrentSessionId) {
        localStorage.removeItem(`kaiso_table_session_${tableId}`);
        setShowTableSessionModal(true);
        return;
      }

      // Validate the cached session is still open (covers old backends + confirms active sessions)
      axios.get(`${API}/public/table-session/${sessionData.session_id}`)
        .then(res => {
          if (res.data?.status === "open") {
            const mergedSession = mergeTableSessionWithDeviceIdentity(res.data, sessionData);
            setTableSession(mergedSession);
            localStorage.setItem(`kaiso_table_session_${tableId}`, JSON.stringify(mergedSession));
          } else {
            localStorage.removeItem(`kaiso_table_session_${tableId}`);
            setShowTableSessionModal(true);
          }
        })
        .catch(() => {
          localStorage.removeItem(`kaiso_table_session_${tableId}`);
          setShowTableSessionModal(true);
        });
    } catch {
      localStorage.removeItem(`kaiso_table_session_${tableId}`);
      setShowTableSessionModal(true);
    }
  }, [isPayAtEnd, tableId, tableCurrentSessionId]);

  const refreshTableSession = async () => {
    if (!tableSession?.session_id) return;
    try {
      const res = await axios.get(`${API}/public/table-session/${tableSession.session_id}`);
      const mergedSession = mergeTableSessionWithDeviceIdentity(res.data, tableSession);
      setTableSession(mergedSession);
      localStorage.setItem(`kaiso_table_session_${tableId}`, JSON.stringify(mergedSession));
    } catch {}
  };

  const startTableSession = async ({ name, phone, guests, marketingConsent = false }) => {
    if (!tableId || !restaurant?.id) {
      return "Configuração de mesa inválida. Tente recarregar a página.";
    }
    setTableSessionLoading(true);
    try {
      const res = await axios.post(`${API}/public/table-session/start`, {
        table_id: tableId,
        restaurant_id: restaurant.id,
        customer_name: name,
        customer_phone: phone,
        guests: guests,
        language: i18n.language || "pt",
        marketing_consent: marketingConsent,
      });
      const sessionData = {
        session_id: res.data.session_id,
        identity_scope: "device",
        customer_name: res.data.customer_name ?? name,
        customer_phone: res.data.customer_phone ?? phone,
        table_number: res.data.table_number,
        orders: [],
        order_count: 0,
        total: 0,
        status: "open",
        created_at: new Date().toISOString(),
      };
      setTableSession(sessionData);
      localStorage.setItem(`kaiso_table_session_${tableId}`, JSON.stringify(sessionData));
      setShowTableSessionModal(false);
      toast.success(i18n.t("tableSession.welcomeToast", "Bem-vindo! Agora você pode fazer pedidos."));
      setTableSessionLoading(false);
      return null; // success
    } catch (err) {
      setTableSessionLoading(false);
      return err.response?.data?.detail || "Erro ao iniciar sessão. Tente novamente.";
    }
  };

  const handleViewOnly = () => {
    if (tableId) {
      sessionStorage.setItem(`kaiso_table_view_only_${tableId}`, "true");
    }
    setShowTableSessionModal(false);
  };

  const requestBill = async () => {
    if (!tableSession?.session_id) return;
    setRequestingBill(true);
    try {
      await axios.post(`${API}/public/table-session/${tableSession.session_id}/request-bill`);
      setBillRequested(true);
      setShowTableOrdersModal(false);
      toast.success("Conta solicitada! Um funcionário virá até você.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao solicitar conta");
    }
    setRequestingBill(false);
  };

  return {
    tableSession, setTableSession,
    showTableSessionModal, setShowTableSessionModal,
    showTableOrdersModal, setShowTableOrdersModal,
    tableSessionLoading, billRequested, setBillRequested, requestingBill,
    startTableSession, refreshTableSession, requestBill, handleViewOnly,
  };
}
